import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Bot, Boxes, Server, ShieldAlert } from "lucide-react";
import type { AppWithAvailability, JobRow } from "@gwrctl/api-client";
import { getJob } from "@gwrctl/shared";
import { client } from "../lib/client.js";
import { useEventListener } from "../lib/events.tsx";
import { useLatestJobResult } from "../lib/jobs.ts";
import { jobTitle } from "../lib/labels.ts";
import { humanizeError } from "../lib/errors.ts";
import { currentVersions, fmtVersion, headline, pendingBump, shortTime } from "../lib/release.ts";
import { fmtAge } from "../lib/time.ts";
import { JobAction } from "../components/JobAction.tsx";
import { AppIcon } from "../components/AppIcon.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import {
  Banner,
  Button,
  Card,
  Dot,
  Empty,
  ErrorState,
  ProgressBar,
  RowLink,
  StatusPill,
  Tile,
  toneColor,
  type Tone,
} from "../components/Ui.tsx";

const ACTIVE = ["queued", "claimed", "running"];

/** Yayın hattı adımları — kartta "son hareket" olarak yalnızca bunlar sayılır. */
const PIPELINE = /^(release\.|build\.|ota\.|web\.|play\.)/;

/** Servis durumu bundan eskiyse gözcü durmuş olabilir; yeşil nokta yanıltmasın. */
const STALE_MS = 10 * 60_000;

/**
 * Acil müdahaleler. Parametreler Bot/Ops sayfalarındaki çağrılarla aynı; onay
 * seviyesi paylaşılan job tanımından okunur (hepsi "click": arıza anında hız gerekir).
 */
const EMERGENCY: { label: string; type: string; params: Record<string, unknown> }[] = [
  { label: "Tüm oynatıcıları durdur", type: "bot.player.stopAll", params: { process: "both" } },
  { label: "Lavalink'i yeniden başlat", type: "bot.pm2.restart", params: { process: "lavalink" } },
  { label: "zapret'i yeniden başlat", type: "vps.service.restart", params: { service: "zapret" } },
];

export function DashboardPage(): ReactNode {
  const queryClient = useQueryClient();
  const now = useNow(30_000);

  const apps = useQuery({ queryKey: ["apps"], queryFn: () => client.apps() });
  const agents = useQuery({ queryKey: ["agents"], queryFn: () => client.agents() });
  const jobs = useQuery({
    queryKey: ["jobs", "recent", 100],
    queryFn: () => client.jobs({ limit: 100 }),
  });

  // Uygulama başına ayrı liste: global son 100 iş gözcünün işleriyle dolup sürüm
  // bilgisini dışarıda bırakabiliyor. Anahtar AppDetail ile aynı → önbellek ortak.
  const appJobs = useQueries({
    queries: (apps.data ?? []).map((app) => ({
      queryKey: ["jobs", app.id],
      queryFn: () => client.jobs({ app: app.id, limit: 40 }),
    })),
  });

  // Aynı anahtar kenar çubuğundaki rozetle ortak (Shell).
  const feedback = useQuery({
    queryKey: ["feedback-counts"],
    queryFn: () => client.feedbackCounts(),
    refetchInterval: 60_000,
    retry: false,
  });

  // Servis durumu ayrı bir uç değil — gözcünün 5 dakikada bir çalıştırdığı işin sonucu.
  const services = useLatestJobResult<Record<string, { active?: string }>>("vps.service.status");

  useEventListener((event) => {
    if (event.t === "job.update") void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    if (event.t === "agent.status") {
      void queryClient.invalidateQueries({ queryKey: ["agents"] });
      void queryClient.invalidateQueries({ queryKey: ["apps"] });
    }
  });

  const active = jobs.data?.filter((j) => ACTIVE.includes(j.status)) ?? [];
  // Gözcünün kendi kontrolleri "hata" sayılmaz; onlar zaten sağlık kartlarında görünüyor.
  const failed = jobs.data?.filter((j) => j.status === "failed" && j.createdBy !== "watchdog") ?? [];
  const appName = (id: string | null): string | undefined =>
    id ? (apps.data?.find((a) => a.id === id)?.displayName ?? id) : undefined;

  // Uyarı şeridi uydurulmuyor: registry'deki gerçek "⚠" notlarının ilki gösteriliyor.
  const warning = apps.data?.flatMap((a) =>
    (a.notes ?? []).filter((n) => n.startsWith("⚠")).map((n) => ({ app: a, note: n })),
  )[0];

  const agentTile = agentSummary(agents.data, agents.isLoading, agents.error);

  const servicesAge = services.job ? now - new Date(services.job.finishedAt ?? services.job.createdAt).getTime() : null;
  const servicesStale = servicesAge !== null && servicesAge > STALE_MS;

  return (
    <>
      <PageHeader
        kicker="genel bakış"
        title="Pano"
        actions={
          <>
            <Link to="/bot" className="btn btn-ghost">
              Botu teşhis et
            </Link>
            <Link to="/jobs" className="btn btn-secondary">
              İş geçmişi
            </Link>
          </>
        }
      />

      <PageBody>
        {warning && (
          <Banner
            kicker="uyarı"
            action={
              <Link to={`/apps/${warning.app.id}`} className="btn btn-secondary btn-sm">
                Uygulamayı aç
              </Link>
            }
          >
            {warning.app.displayName} · {warning.note.replace(/^⚠\s*/, "")}
          </Banner>
        )}

        {feedback.data?.total ? (
          <Banner
            tone="info"
            kicker="geri bildirim"
            action={
              <Link to="/feedback" className="btn btn-secondary btn-sm">
                İncele
              </Link>
            }
          >
            {feedback.data.total} oyuncu gönderisi onay bekliyor
            {feedback.data.apps.some((a) => a.reported) ? " · bildirilen gönderi var" : ""}
          </Banner>
        ) : null}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile
            label="Çalışan iş"
            icon={Activity}
            to="/jobs?status=running"
            value={jobs.error ? "?" : jobs.isLoading ? "…" : active.length}
            note={jobs.error ? "okunamadı" : active.length ? "canlı log açık" : "kuyruk boş"}
            tone={jobs.error ? "danger" : jobs.isLoading ? "idle" : active.length ? "warn" : "accent"}
          />
          <Tile
            label="Bağlı agent"
            icon={Server}
            to="/security"
            value={agentTile.value}
            note={agentTile.note}
            tone={agentTile.tone}
          />
          <Tile
            label="Son hatalar"
            icon={AlertTriangle}
            to="/jobs?status=failed"
            value={jobs.error ? "?" : jobs.isLoading ? "…" : failed.length}
            note={
              jobs.error ? "okunamadı" : failed.length ? "son 100 iş içinde" : jobs.isLoading ? "" : "temiz"
            }
            tone={jobs.error ? "danger" : jobs.isLoading ? "idle" : failed.length ? "danger" : "ok"}
          />
          <Tile
            label="Uygulama"
            icon={Boxes}
            value={apps.error ? "?" : apps.isLoading ? "…" : (apps.data?.length ?? 0)}
            note={apps.error ? "okunamadı" : "registry'de tanımlı"}
            tone={apps.error ? "danger" : "accent"}
          />
        </div>

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
          <div className="grid min-w-0 gap-4">
            {/* Çalışan iş yoksa kart yer kaplamasın; kutucuk zaten "kuyruk boş" diyor. */}
            {active.length > 0 && (
              <Card title="Çalışan işler" hint="canlı" flush>
                {active.map((job) => (
                  <RowLink key={job.id} to={`/jobs/${job.id}`}>
                    <div className="grid w-full min-w-0 gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-neutral-100">{jobTitle(job.type)}</span>
                        {job.appId && <span className="tag tag-neutral">{appName(job.appId)}</span>}
                        <span className="mono ml-auto text-2xs text-neutral-500">
                          {new Date(job.createdAt).toLocaleTimeString("tr-TR")}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 truncate text-xs text-neutral-300">
                          {job.step ?? "başlıyor…"}
                        </span>
                        <span className="ml-auto">
                          <StatusPill status={job.status} />
                        </span>
                      </div>
                      <ProgressBar pct={job.pct} />
                    </div>
                  </RowLink>
                ))}
              </Card>
            )}

            <Card title="Uygulamalar" icon={Boxes} flush>
              {apps.error ? (
                <ErrorState error={apps.error} onRetry={() => void apps.refetch()} />
              ) : apps.isLoading ? (
                <Empty>Yükleniyor…</Empty>
              ) : apps.data?.length ? (
                <div className="grid sm:grid-cols-2">
                  {apps.data.map((app, i) => {
                    const q = appJobs[i];
                    return (
                      <AppCard
                        key={app.id}
                        app={app}
                        jobs={q?.data}
                        loading={q?.isLoading ?? true}
                        error={q?.error ?? null}
                      />
                    );
                  })}
                </div>
              ) : (
                <Empty>Registry'de uygulama yok</Empty>
              )}
            </Card>

            <Card
              title="Son işler"
              flush
              action={
                <Link to="/jobs" className="text-xs text-accent-300 hover:underline">
                  Tümü →
                </Link>
              }
            >
              {jobs.error ? (
                <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />
              ) : jobs.isLoading ? (
                <Empty>Yükleniyor…</Empty>
              ) : jobs.data?.length ? (
                jobs.data.slice(0, 8).map((job) => (
                  <RowLink key={job.id} to={`/jobs/${job.id}`} className="flex-wrap !gap-y-1 py-2.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-neutral-200">
                      {jobTitle(job.type)}
                      {job.appId && <span className="text-neutral-500"> · {appName(job.appId)}</span>}
                    </span>
                    <StatusPill status={job.status} />
                    <span className="mono w-[86px] text-right text-2xs text-neutral-600">
                      {shortTime(job.createdAt)}
                    </span>
                  </RowLink>
                ))
              ) : (
                <Empty>Henüz iş yok</Empty>
              )}
            </Card>
          </div>

          <div className="grid min-w-0 gap-4">
            <Card
              title="VPS · servisler"
              icon={Server}
              hint={
                servicesAge !== null ? (
                  <span className={servicesStale ? "text-warn" : undefined}>
                    {fmtAge(servicesAge)}
                    {servicesStale ? " · eski" : ""}
                  </span>
                ) : undefined
              }
              action={
                <Link to="/ops" className="text-xs text-accent-300 hover:underline">
                  Sunucu →
                </Link>
              }
              flush
            >
              {services.loadError ? (
                <ErrorState error={services.loadError} onRetry={services.reload} compact />
              ) : services.data ? (
                <>
                  {servicesStale && (
                    <p className="px-4 pt-3 text-2xs text-warn">
                      Son kontrol 10 dakikadan eski — gözcü ya da VPS agent'ı durmuş olabilir.
                    </p>
                  )}
                  {Object.entries(services.data).map(([name, info]) => {
                    const ok = String(info.active ?? "").startsWith("active");
                    // Eski veride yeşil göstermek yanlış güven verir → sönük.
                    const tone: Tone = ok ? (servicesStale ? "warn" : "ok") : "danger";
                    return (
                      <div key={name} className="row py-2">
                        <Dot tone={tone} />
                        <span className="mono text-xs text-neutral-200">{name}</span>
                        <span className="ml-auto text-2xs text-neutral-500">{info.active ?? "?"}</span>
                      </div>
                    );
                  })}
                </>
              ) : (
                <Empty>{services.isLoading ? "Yükleniyor…" : "Henüz kontrol çalışmadı"}</Empty>
              )}
            </Card>

            <Card
              title={<span className="text-danger">Acil müdahale</span>}
              icon={ShieldAlert}
              flush
              style={{ borderColor: "color-mix(in srgb, var(--color-danger) 28%, transparent)" }}
            >
              <p className="px-4 pt-3 text-2xs text-neutral-500">
                İki tıkla çalışır, sonuç bildirim olarak gelir · telefondan da erişilebilir
              </p>
              <div className="grid gap-2 p-3">
                {EMERGENCY.map((item) => (
                  <div
                    key={item.type}
                    className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[var(--radius-sm)] border border-neutral-800 bg-white/[0.02] px-3 py-2.5"
                  >
                    <div className="grid min-w-0 flex-1 gap-0.5">
                      <span className="text-sm text-neutral-200">{item.label}</span>
                      {getJob(item.type)?.warning && (
                        <span className="text-2xs text-neutral-500">{getJob(item.type)?.warning}</span>
                      )}
                    </div>
                    <JobAction
                      type={item.type}
                      params={item.params}
                      label={item.label}
                      variant="danger"
                      size="sm"
                    >
                      Çalıştır
                    </JobAction>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Hızlı erişim" icon={Bot} flush>
              <RowLink to="/bot">
                <span className="text-sm text-neutral-200">Discord botları</span>
                <span className="ml-auto text-2xs text-neutral-500">pm2 · oynatıcılar · teşhis</span>
              </RowLink>
              <RowLink to="/ops">
                <span className="text-sm text-neutral-200">Sunucu</span>
                <span className="ml-auto text-2xs text-neutral-500">kaynaklar · servisler · yedek</span>
              </RowLink>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

/**
 * Agent kutusunun içeriği. Sorgu hata verdiyse 0/0 "hepsi açık" DEĞİLDİR —
 * bilinmiyor demektir.
 */
function agentSummary(
  agents: { online: boolean }[] | undefined,
  loading: boolean,
  error: unknown,
): { value: string; note: string; tone: Tone } {
  if (error) return { value: "?", note: "okunamadı", tone: "danger" };
  if (loading || !agents) return { value: "…", note: "yükleniyor", tone: "idle" };
  if (agents.length === 0) return { value: "0", note: "kayıtlı agent yok", tone: "warn" };
  const online = agents.filter((a) => a.online).length;
  const all = online === agents.length;
  return {
    value: `${online}/${agents.length}`,
    note: all ? "hepsi açık" : online === 0 ? "hepsi kapalı" : `${agents.length - online} kapalı`,
    tone: all ? "ok" : online === 0 ? "danger" : "warn",
  };
}

function AppCard({
  app,
  jobs,
  loading,
  error,
}: {
  app: AppWithAvailability;
  jobs: JobRow[] | undefined;
  loading: boolean;
  error: unknown;
}): ReactNode {
  const ready = Object.values(app.availability).filter(Boolean).length;
  const total = Object.keys(app.availability).length;
  const tone: Tone = ready === 0 ? "idle" : ready === total ? "ok" : "warn";

  const current = currentVersions(jobs);
  const version = current ? headline(current.versions) : undefined;
  const last = jobs?.find((j) => PIPELINE.test(j.type));
  const bump = pendingBump(jobs);

  return (
    <Link
      to={`/apps/${app.id}`}
      className="row-hover grid min-w-0 content-start gap-2 border-b border-neutral-900 px-4 py-3.5 sm:odd:border-r"
    >
      <div className="flex items-center gap-2">
        <AppIcon id={app.id} name={app.displayName} size={28} />
        <span className="truncate font-medium text-neutral-100">{app.displayName}</span>
        <Dot tone={tone} />
        <span className="ml-auto text-2xs text-neutral-600">{app.platform}</span>
      </div>

      {error ? (
        <span className="text-2xs text-danger">İş geçmişi okunamadı: {humanizeError(error)}</span>
      ) : loading ? (
        <span className="text-2xs text-neutral-600">Yükleniyor…</span>
      ) : (
        <>
          <span className="mono text-xs text-neutral-300">
            {version ? fmtVersion(version.name, version.code) : "sürüm bilinmiyor"}
          </span>
          {last ? (
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-neutral-500">
              <span className="min-w-0 truncate">{jobTitle(last.type)}</span>
              <StatusPill status={last.status} />
              <span className="mono">{shortTime(last.createdAt)}</span>
            </div>
          ) : (
            <span className="text-2xs text-neutral-600">Yayın hattında hareket yok</span>
          )}
          {bump && (
            <span className="text-2xs text-warn">
              {fmtVersion(bump.version, bump.versionCode)} yükseltildi, henüz derlenmedi
            </span>
          )}
        </>
      )}

      <span className="text-2xs" style={{ color: toneColor[tone] }}>
        {ready}/{total} yetenek hazır
      </span>
    </Link>
  );
}

/** Veri yaşı gibi göreli süreler dakikada bir güncellensin diye. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
